/**
 * Shared API utilities for NorthBuilt Compass
 *
 * Common functions used across API clients.
 */

import CONFIG from '../config';

/**
 * Default request timeout in milliseconds
 */
export const DEFAULT_TIMEOUT = 30000; // 30 seconds

/**
 * Debug logger - only logs when CONFIG.DEBUG is true
 */
export function debugLog(
  message: string,
  data?: Record<string, unknown>,
): void {
  if (CONFIG.DEBUG) {
    console.log(message, data ?? '');
  }
}

/**
 * Create an AbortSignal with timeout, combining with optional external signal
 */
export function createTimeoutSignal(
  externalSignal?: AbortSignal,
  timeout: number = DEFAULT_TIMEOUT,
): AbortSignal {
  const timeoutSignal = AbortSignal.timeout(timeout);

  if (!externalSignal) {
    return timeoutSignal;
  }

  // Combine external signal with timeout using AbortSignal.any
  return AbortSignal.any([externalSignal, timeoutSignal]);
}

/**
 * Handle HTTP response errors with consistent messaging.
 *
 * @param response - The fetch Response object
 * @param parseErrorBody - If true, attempts to parse error message from response body
 */
export async function handleHttpError(
  response: Response,
  parseErrorBody: boolean = false,
): Promise<void> {
  if (response.status === 401) {
    throw new Error('Authentication required. Please sign in.');
  }
  if (!response.ok) {
    let errorMessage: string | null = null;

    if (parseErrorBody) {
      try {
        const data = await response.json();
        if (data.error) {
          errorMessage = data.error;
        }
      } catch {
        // If body parsing fails, use status text
      }
    }

    throw new Error(
      errorMessage || `HTTP ${response.status}: ${response.statusText}`,
    );
  }
}

/**
 * Wrap network errors with user-friendly messages
 */
export function handleNetworkError(error: unknown): never {
  if (error instanceof TypeError) {
    throw new Error(
      'Network error. Please check your connection and try again.',
    );
  }
  throw error;
}

/**
 * Get the base API URL without trailing slash
 */
export function getBaseUrl(): string {
  return CONFIG.REST_API_ENDPOINT.endsWith('/')
    ? CONFIG.REST_API_ENDPOINT.slice(0, -1)
    : CONFIG.REST_API_ENDPOINT;
}
